import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { fmtDate } from '../../dateUtil';
import { formatCurrency } from '../../currencyUtil';

export default function BuyerDealList() {
  const [deals, setDeals]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/deals?buyer=true')
      .then(d  => { if (mounted) setDeals(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Available Deals</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {deals.length} deal{deals.length !== 1 ? 's' : ''} available
        </p>
      </div>

      {!deals.length ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No deals available</h3>
          <p className="text-sm text-slate-400 dark:text-slate-500 max-w-sm leading-relaxed">
            Sellers have to add your lot before their deals appear here. Share your lot code —
            it is on your <span className="font-medium text-slate-500 dark:text-slate-400">Settings</span> page —
            with the dealers you buy from.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {deals.map((deal) => (
            <Link
              key={deal.uuid}
              to={`/buyer/deals/${deal.uuid}`}
              className="card p-4 sm:p-5 block cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl hidden sm:flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-800 dark:text-slate-100">{deal.name || `Deal #${deal.id}`}</p>
                    {Number(deal.my_accepted_bids) > 0 && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-800">
                        Bid accepted
                      </span>
                    )}
                    {Number(deal.my_accepted_bids) === 0 && Number(deal.my_pending_bids) > 0 && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-800">
                        {deal.my_pending_bids} bid{deal.my_pending_bids !== '1' ? 's' : ''} pending
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {deal.transaction_type || '—'} &middot; {deal.pickup_or_delivery || '—'}
                    {deal.end_date ? ` · Ends ${fmtDate(deal.end_date, { month: 'short', day: 'numeric' })}` : ''}
                  </p>
                </div>
                {deal.total_price && (
                  <span className="text-slate-700 dark:text-slate-300 font-semibold tabular-nums">
                    ${formatCurrency(deal.total_price)}
                  </span>
                )}
                <svg className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-400 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
